import { createTheme } from '@mui/material/styles';

export const LUTIQ_COLORS = {
  paper: '#FAF8F3',
  card: '#FFFFFF',
  tarmac: '#F0EDE5',
  ink: '#191C33',
  steel: '#565A6E',
  gravel: '#6E7287',
  surfaceHover: '#F7F4ED',
  hairline: '#E4E0D5',
  hairlineStrong: '#D8D3C4',
  night: '#0B0D1F',
  pitWall: '#141838',
  chalk: '#EAECF6',
  gravelLight: '#9BA0B5',
  mutedOnDark: '#777D96',
  gridline: '#1C2140',
  indigo: '#4F46E5',
  indigoHover: '#4338CA',
  slipstream: '#818CF8',
  finishFade: '#C7D2FE',
  success: '#087F4C',
  verifiedOnDark: '#13D479',
  error: '#BA2E38',
  errorOnDark: '#FF9AA3',
  warningOnDark: '#FBBF24',
} as const;

export const TEXT_FONT = '"Supreme", -apple-system, "Segoe UI", Roboto, Arial, sans-serif';
export const MONO_FONT = '"Martian Mono", "SF Mono", "Roboto Mono", Consolas, monospace';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: LUTIQ_COLORS.indigo,
      light: LUTIQ_COLORS.slipstream,
      dark: LUTIQ_COLORS.indigoHover,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: LUTIQ_COLORS.slipstream,
      light: LUTIQ_COLORS.finishFade,
      dark: LUTIQ_COLORS.indigo,
    },
    background: {
      default: LUTIQ_COLORS.paper,
      paper: LUTIQ_COLORS.card,
    },
    text: {
      primary: LUTIQ_COLORS.ink,
      secondary: LUTIQ_COLORS.steel,
      disabled: LUTIQ_COLORS.gravel,
    },
    divider: LUTIQ_COLORS.hairline,
    success: {
      main: LUTIQ_COLORS.success,
      light: '#E8F7EF',
      dark: '#065C38',
    },
    error: {
      main: LUTIQ_COLORS.error,
      light: '#FDECEF',
      dark: '#8B202A',
    },
    warning: {
      main: '#925B06',
      light: '#FFF6D6',
      dark: '#704300',
    },
    info: {
      main: LUTIQ_COLORS.indigo,
      light: '#EEF0FF',
      dark: '#3730A3',
    },
    action: {
      hover: 'rgba(79, 70, 229, 0.04)',
      selected: 'rgba(79, 70, 229, 0.08)',
      focus: 'rgba(79, 70, 229, 0.08)',
    },
  },
  typography: {
    fontFamily: TEXT_FONT,
    h1: { fontWeight: 700, letterSpacing: '-0.015em', lineHeight: 1.2, fontSize: '2.25rem', color: LUTIQ_COLORS.ink },
    h2: { fontWeight: 700, letterSpacing: '-0.015em', lineHeight: 1.25, fontSize: '1.875rem', color: LUTIQ_COLORS.ink },
    h3: { fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.3, fontSize: '1.5rem', color: LUTIQ_COLORS.ink },
    h4: { fontWeight: 500, letterSpacing: '0', lineHeight: 1.3, fontSize: '1.25rem', color: LUTIQ_COLORS.ink },
    h5: { fontWeight: 500, letterSpacing: '0', lineHeight: 1.4, fontSize: '1.125rem', color: LUTIQ_COLORS.ink },
    h6: { fontWeight: 500, letterSpacing: '0', lineHeight: 1.4, fontSize: '1rem', color: LUTIQ_COLORS.ink },
    subtitle1: { fontWeight: 500, fontSize: '0.875rem', lineHeight: 1.5 },
    subtitle2: { fontWeight: 500, fontSize: '0.8125rem', letterSpacing: '0.01em', lineHeight: 1.5 },
    body1: { fontWeight: 400, fontSize: '0.875rem', lineHeight: 1.6 },
    body2: { fontWeight: 400, fontSize: '0.8125rem', lineHeight: 1.5 },
    caption: { fontWeight: 400, fontSize: '0.75rem', letterSpacing: '0.02em', lineHeight: 1.5 },
    overline: {
      fontWeight: 500,
      fontSize: '0.75rem',
      letterSpacing: '0.08em',
      lineHeight: 1.35,
      textTransform: 'uppercase',
    },
    button: { fontWeight: 500, fontSize: '0.8125rem', letterSpacing: '0', textTransform: 'none' },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0 1px 2px rgba(25, 28, 51, 0.04)',
    '0 2px 8px rgba(25, 28, 51, 0.05)',
    '0 6px 18px rgba(25, 28, 51, 0.07)',
    '0 12px 30px rgba(25, 28, 51, 0.08)',
    '0 20px 50px rgba(25, 28, 51, 0.1)',
    ...Array(19).fill('0 20px 50px rgba(25, 28, 51, 0.1)'),
  ] as any,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: LUTIQ_COLORS.paper,
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${LUTIQ_COLORS.hairline}`,
          backgroundColor: LUTIQ_COLORS.card,
          boxShadow: '0 1px 2px rgba(25, 28, 51, 0.04)',
        },
        outlined: {
          borderRadius: 8,
          backgroundColor: LUTIQ_COLORS.card,
          boxShadow: 'none',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.8125rem',
          borderRadius: 8,
          paddingInline: 16,
          letterSpacing: 0,
        },
        contained: {
          backgroundColor: LUTIQ_COLORS.indigo,
          boxShadow: '0 1px 2px rgba(25, 28, 51, 0.08)',
          '&:hover': {
            backgroundColor: LUTIQ_COLORS.indigoHover,
            boxShadow: '0 2px 5px rgba(25, 28, 51, 0.12)',
          },
        },
        outlined: {
          borderColor: LUTIQ_COLORS.hairlineStrong,
          color: LUTIQ_COLORS.ink,
          '&:hover': {
            backgroundColor: 'rgba(79, 70, 229, 0.04)',
            borderColor: '#4F46E5',
            color: '#4F46E5',
          },
        },
        text: {
          color: LUTIQ_COLORS.ink,
          '&:hover': {
            backgroundColor: 'rgba(79, 70, 229, 0.04)',
          },
        },
        sizeSmall: {
          padding: '6px 12px',
          fontSize: '0.75rem',
        },
        sizeLarge: {
          padding: '12px 24px',
          fontSize: '0.875rem',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: LUTIQ_COLORS.card,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: LUTIQ_COLORS.gravel,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#4F46E5',
            borderWidth: '1.5px',
            boxShadow: '0 0 0 3px rgba(79, 70, 229, 0.1)',
          },
        },
        notchedOutline: {
          borderColor: LUTIQ_COLORS.hairlineStrong,
        },
        input: {
          color: LUTIQ_COLORS.ink,
          '&::placeholder': {
            color: LUTIQ_COLORS.gravel,
            opacity: 1,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: LUTIQ_COLORS.steel,
          '&.Mui-focused': {
            color: '#4F46E5',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          fontSize: '0.75rem',
          height: 24,
        },
        sizeSmall: {
          height: 22,
          fontSize: '0.6875rem',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          backgroundColor: LUTIQ_COLORS.card,
          border: `1px solid ${LUTIQ_COLORS.hairline}`,
          boxShadow: '0 20px 60px rgba(25, 28, 51, 0.12), 0 8px 24px rgba(25, 28, 51, 0.08)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '20px 24px',
          fontSize: '1rem',
          fontWeight: 600,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '0 24px 24px',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: LUTIQ_COLORS.tarmac,
            color: LUTIQ_COLORS.steel,
            fontWeight: 500,
            fontSize: '0.75rem',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            borderBottom: `1px solid ${LUTIQ_COLORS.hairline}`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          padding: '12px 16px',
          borderBottom: `1px solid ${LUTIQ_COLORS.hairline}`,
          color: LUTIQ_COLORS.ink,
          fontVariantNumeric: 'tabular-nums',
          '&.MuiTableCell-alignRight:not(.MuiTableCell-head)': {
            fontFamily: MONO_FONT,
            '& .MuiButton-root, & .MuiChip-root': {
              fontFamily: TEXT_FONT,
            },
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: LUTIQ_COLORS.surfaceHover,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 4,
          backgroundColor: LUTIQ_COLORS.hairline,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: LUTIQ_COLORS.hairline,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          color: LUTIQ_COLORS.steel,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: LUTIQ_COLORS.card,
          border: `1px solid ${LUTIQ_COLORS.hairline}`,
          boxShadow: '0 10px 40px rgba(25, 28, 51, 0.08), 0 2px 8px rgba(25, 28, 51, 0.04)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(79, 70, 229, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(79, 70, 229, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(79, 70, 229, 0.12)',
            },
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: LUTIQ_COLORS.card,
          border: `1px solid ${LUTIQ_COLORS.hairline}`,
          boxShadow: '0 10px 40px rgba(25, 28, 51, 0.08), 0 2px 8px rgba(25, 28, 51, 0.04)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: LUTIQ_COLORS.pitWall,
          color: LUTIQ_COLORS.chalk,
          border: `1px solid ${LUTIQ_COLORS.gridline}`,
          fontSize: '0.75rem',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: LUTIQ_COLORS.indigo,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: LUTIQ_COLORS.steel,
          '&.Mui-selected': {
            color: LUTIQ_COLORS.indigo,
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        track: {
          backgroundColor: LUTIQ_COLORS.hairlineStrong,
        },
      },
    },
  },
});

export const sidebarTheme = {
  width: 240,
  collapsedWidth: 64,
  bg: LUTIQ_COLORS.night,
  borderColor: LUTIQ_COLORS.gridline,
  text: {
    primary: LUTIQ_COLORS.chalk,
    secondary: LUTIQ_COLORS.gravelLight,
    muted: LUTIQ_COLORS.mutedOnDark,
    active: '#FFFFFF',
  },
  hover: {
    bg: LUTIQ_COLORS.pitWall,
  },
  active: {
    bg: 'rgba(79, 70, 229, 0.14)',
    indicator: LUTIQ_COLORS.slipstream,
  },
  groupHeader: {
    color: LUTIQ_COLORS.gravelLight,
    fontSize: '0.6875rem',
    fontWeight: 500,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
  },
  navItem: {
    fontSize: '0.8125rem',
    fontWeight: 400,
    activeWeight: 500,
    borderRadius: 6,
    padding: '8px 12px',
  },
  selector: {
    bg: LUTIQ_COLORS.pitWall,
    border: LUTIQ_COLORS.gridline,
    hoverBorder: 'rgba(129, 140, 248, 0.45)',
  },
};

export default theme;
